/**
 * Record Normalization Utilities
 * Helpers to create normalizeRecord functions with less boilerplate
 */

/**
 * Field mapping configuration
 */
export interface FieldMapping {
    /** Target field name in the normalized record */
    target: string;
    /** Source field name in the raw data (defaults to target if not specified) */
    source?: string;
    /** Transform function to apply to the value */
    transform?: (value: any) => any;
}

/**
 * Common field transformations
 */
export const FieldTransforms = {
    /** Convert to string, handle null/undefined */
    toString: (v: any) => (v != null ? String(v) : undefined),

    /** Convert ID fields to string */
    toStringId: (v: any) => (v != null ? String(v) : undefined),

    /** Convert to number, handle null/undefined */
    toNumber: (v: any) => (v != null ? Number(v) : undefined),

    /** Convert to boolean */
    toBoolean: (v: any) => Boolean(v),

    /** Identity - no transformation */
    identity: (v: any) => v,
};

function unwrapDocumentPayload(raw: any): any {
    if (!raw || typeof raw !== 'object') {
        return raw;
    }

    const maybeDoc = (raw as Record<string, unknown>)._doc;
    if (maybeDoc && typeof maybeDoc === 'object' && !Array.isArray(maybeDoc)) {
        return maybeDoc;
    }

    return raw;
}

/**
 * Create a normalizeRecord function with automatic ID handling and field mapping
 * Reduces boilerplate for creating normalizers
 *
 * @example
 * ```ts
 * const normalizeRecord = createNormalizer<ItemRecord>({
 *   idFields: ['lotId', 'tenderId', 'categoryId'],
 *   fields: [
 *     'itemNumber', 'title', 'description',
 *     { target: 'quantity', transform: FieldTransforms.toNumber },
 *   ]
 * });
 * ```
 */
export function createNormalizer<TRecord>(config: {
    /** Fields that should be converted to string IDs (e.g., 'categoryId', 'parentId') */
    idFields?: string[];
    /** Field names or mappings to include in normalized record */
    fields?: (string | FieldMapping)[];
    /** Custom normalization logic to apply after auto-mapping */
    customize?: (normalized: Partial<TRecord>, raw: any) => void;
}): (raw: any) => TRecord {
    const { idFields = [], fields = [], customize } = config;

    return (raw: any): TRecord => {
        if (!raw) return raw;

        const payload = unwrapDocumentPayload(raw);

        const normalized: any = {
            // Always normalize the main ID
            id: String(payload.id || payload._id),
        };

        // Handle ID fields with toString
        idFields.forEach((field) => {
            if (payload[field] !== undefined) {
                normalized[field] = payload[field] != null ? String(payload[field]) : undefined;
            }
        });

        // Handle regular fields
        fields.forEach((fieldConfig) => {
            if (typeof fieldConfig === 'string') {
                // Simple field name - direct copy
                if (payload[fieldConfig] !== undefined) {
                    normalized[fieldConfig] = payload[fieldConfig];
                }
            } else {
                // Field mapping with optional transform
                const { target, source: sourceField = target, transform = FieldTransforms.identity } = fieldConfig;
                if (payload[sourceField] !== undefined) {
                    normalized[target] = transform(payload[sourceField]);
                }
            }
        });

        // Apply custom logic if provided
        if (customize) {
            customize(normalized, payload);
        }

        return normalized as TRecord;
    };
}

/**
 * Create a simple normalizer that just converts id to string and copies all fields
 * Use when no special transformation is needed
 *
 * @example
 * ```ts
 * const normalizeRecord = createSimpleNormalizer<CategoryRecord>();
 * ```
 */
export function createSimpleNormalizer<TRecord>(): (raw: any) => TRecord {
    return (raw: any): TRecord => {
        if (!raw) return raw;
        const payload = unwrapDocumentPayload(raw);
        return {
            ...payload,
            id: String(payload.id || payload._id),
        } as TRecord;
    };
}
